import { Person, PersonCategory } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useCreatePerson, usePersons } from '@/hooks/api-hooks';
import { Button, Loader, TextInput, NumberInput, ActionIcon, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconId, IconMail, IconPhone, IconPhonePlus, IconSearch, IconX, IconUserPlus, IconPencil } from '@tabler/icons-react';
import { TagIcon } from '@/lib/tag-utils';
import { notify } from '@/lib/notify';
import { PaxCategoryBadge, PaxCategorySelect } from './snippets';

const MIN_CHARS_TO_SEARCH = 2;

interface Props {
    onCreate?: (pax: Person) => void;
    onSelect?: (pax: Person) => void;
    onClose?: () => void;
    preloadName?: string;
}

export default function CreateOrSelectPerson({
    onCreate,
    onSelect,
    preloadName,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [name, setName] = useState(preloadName || '');
    const [activeSearchValue, setActiveSearchValue] = useState(preloadName || '');

    const [category, setCategory] = useState<PersonCategory | null>('pax');
    const [adults, setAdults] = useState<number | string>('');
    const [children, setChildren] = useState<number | string>('');
    const [infants, setInfants] = useState<number | string>('');
    const [seniors, setSeniors] = useState<number | string>('');
    const [guests, setGuests] = useState<number | string>('');
    const [email, setEmail] = useState('');
    const [showEmail, setShowEmail] = useState(false);
    const [phone, setPhone] = useState('');
    const [phone_2, setPhone2] = useState('');
    const [showPhone2, setShowPhone2] = useState(false);
    const [document_number, setDocument] = useState('');
    const [showDoc, setShowDoc] = useState(false);
    const [observations, setObservations] = useState('');

    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

    const nameInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const phone2InputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);

    const getSearchQuery = () => {
        if (activeSearchValue.trim().length >= MIN_CHARS_TO_SEARCH) return activeSearchValue.trim();
        if (name.trim().length >= MIN_CHARS_TO_SEARCH) return name.trim();
        if (document_number.trim().length >= MIN_CHARS_TO_SEARCH) return document_number.trim();
        if (phone.trim().length >= MIN_CHARS_TO_SEARCH) return phone.trim();
        if (phone_2.trim().length >= MIN_CHARS_TO_SEARCH) return phone_2.trim();
        if (email.trim().length >= MIN_CHARS_TO_SEARCH) return email.trim();
        return '';
    };

    const searchQuery = getSearchQuery();
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 250);

    const {
        data: searchResponse,
        isLoading: isSearching
    } = usePersons({
        query: debouncedSearchQuery,
    }, {
        enabled: debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH
    });

    const matchingPaxs: Person[] = (searchResponse as any)?.rows || (searchResponse as any)?.data || [];

    const createPersonMutation = useCreatePerson();

    const resetForm = () => {
        setName('');
        setCategory('pax');
        setAdults('');
        setChildren('');
        setInfants('');
        setSeniors('');
        setGuests('');
        setEmail('');
        setShowEmail(false);
        setPhone('');
        setPhone2('');
        setShowPhone2(false);
        setDocument('');
        setShowDoc(false);
        setObservations('');
        setActiveSearchValue('');
        setHighlightedIndex(-1);
        setTimeout(() => nameInputRef.current?.focus(), 0);
    };

    const handleSelectPax = (selectedPax: Person) => {
        onCreate?.(selectedPax);
        onSelect?.(selectedPax);
        resetForm();
    };

    const save = () => {
        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length < MIN_CHARS_TO_SEARCH || createPersonMutation.isPending) return;

        createPersonMutation.mutate({
            name: trimmedName,
            email: email.trim() || undefined,
            category: category || undefined,
            adults: Number(adults) || 0,
            children: Number(children) || 0,
            infants: Number(infants) || 0,
            seniors: Number(seniors) || 0,
            guests: Number(guests) || 0,
            phone: phone.trim() || undefined,
            phone_2: phone_2.trim() || undefined,
            document_number: document_number.trim() || undefined,
            observations: observations.trim() || undefined,
        }, {
            onSuccess: (result) => {
                handleSelectPax(result.data);
            },
            onError: (err: any) => {
                const errorKey = err?.response?.data?.error || err?.error || err?.message || 'error';
                notify.error(t(errorKey), t('error'));
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown' && matchingPaxs.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < matchingPaxs.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp' && matchingPaxs.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : matchingPaxs.length - 1));
        } else if (e.key === 'Escape') {
            if (highlightedIndex !== -1) {
                e.preventDefault();
                setHighlightedIndex(-1);
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < matchingPaxs.length) {
                handleSelectPax(matchingPaxs[highlightedIndex]);
            } else {
                save();
            }
        }
    };

    useEffect(() => {
        if (preloadName) {
            setName(preloadName);
            setActiveSearchValue(preloadName);
        }
    }, [preloadName]);

    const canCreateNewPax = name.trim().length >= MIN_CHARS_TO_SEARCH;

    return (
        <div className="w-full max-w-full grid grid-cols-1 lg:grid-cols-12 gap-2.5 items-stretch">
            {/* Left Side: Always-visible New Person Form */}
            <div className="lg:col-span-8 p-2.5 bg-slate-50/90 rounded-lg border border-slate-200/90 flex flex-col justify-between gap-2">
                {/* Row 1: Name + Headcounts */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 w-full">
                    {/* <PaxCategorySelect
                        size="xs"
                        value={category}
                        onChange={(val) => setCategory(val)}
                        disabled={createPersonMutation.isPending}
                        onFocus={() => setHighlightedIndex(-1)}
                        className="w-28 shrink-0"
                    /> */}
                    <TextInput
                        ref={nameInputRef}
                        size="xs"
                        placeholder={t('name')}
                        value={name}
                        disabled={createPersonMutation.isPending}
                        leftSection={<IconUserPlus size={14} className="text-slate-500" />}
                        onChange={(e) => {
                            const val = e.target.value;
                            setName(val);
                            setActiveSearchValue(val);
                            setHighlightedIndex(-1);
                        }}
                        onKeyDown={handleKeyDown}
                        className="flex-1 min-w-[150px] font-semibold"
                    />
                    {/* <TextInput
                        size="xs"
                        type="text"
                        placeholder={t('person_notes')}
                        leftSection={<IconPencil size={14} className="text-gray-400" />}
                        value={observations}
                        disabled={createPersonMutation.isPending}
                        onFocus={() => setHighlightedIndex(-1)}
                        onChange={(e) => {
                            setObservations(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        className="font-semibold flex-1 min-w-[140px]"
                    /> */}
                </div>
                {/* person sizes */}
                <div className="flex items-center gap-1 shrink-0">
                    {([
                        { key: 'adults', val: adults, setter: setAdults },
                        { key: 'children', val: children, setter: setChildren },
                        { key: 'seniors', val: seniors, setter: setSeniors },
                        { key: 'infants', val: infants, setter: setInfants },
                        { key: 'guests', val: guests, setter: setGuests },
                    ] as const).map(({ key, val, setter }) => (
                        <Tooltip key={key} label={t(key)} position="top" withArrow openDelay={200}>
                            <div>
                                <NumberInput
                                    size="xs"
                                    min={0}
                                    hideControls
                                    leftSection={<PaxCategoryBadge category={key} />}
                                    leftSectionWidth={24}
                                    value={val}
                                    disabled={createPersonMutation.isPending}
                                    onFocus={() => setHighlightedIndex(-1)}
                                    onChange={(v) => setter(v)}
                                    onKeyDown={handleKeyDown}
                                    className="font-semibold flex-1"
                                    styles={{
                                        input: {
                                            paddingLeft: 22,
                                            paddingRight: 2,
                                            textAlign: 'center',
                                        },
                                    }}
                                />
                            </div>
                        </Tooltip>
                    ))}
                </div>

                {/* Row 2: Phone + Expandable Phone 2, Document, Email */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5">
                    <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                        <TextInput
                            ref={phoneInputRef}
                            size="xs"
                            type="tel"
                            placeholder={t('phone')}
                            leftSection={<IconPhone size={14} className="text-blue-500" />}
                            value={phone}
                            disabled={createPersonMutation.isPending}
                            onFocus={() => setHighlightedIndex(-1)}
                            onChange={(e) => {
                                const val = e.target.value;
                                setPhone(val);
                                setActiveSearchValue(val);
                                setHighlightedIndex(-1);
                            }}
                            onKeyDown={handleKeyDown}
                            className="font-semibold flex-1 min-w-0"
                        />
                        {!showPhone2 && (
                            <Tooltip label={t('add_second_phone')} withArrow>
                                <Button
                                    size="compact-xs"
                                    radius="md"
                                    variant="light"
                                    color="blue"
                                    px={6}
                                    className="shrink-0 h-[28px]"
                                    onClick={() => {
                                        setShowPhone2(true);
                                        setTimeout(() => phone2InputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconPhonePlus size={15} />
                                </Button>
                            </Tooltip>
                        )}
                        {!showDoc && (
                            <Tooltip label={t('id_number')} withArrow>
                                <Button
                                    size="compact-xs"
                                    radius="md"
                                    variant="light"
                                    color="blue"
                                    px={6}
                                    className="shrink-0 h-[28px]"
                                    onClick={() => {
                                        setShowDoc(true);
                                        setTimeout(() => docInputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconId size={15} />
                                </Button>
                            </Tooltip>
                        )}
                        {!showEmail && (
                            <Tooltip label={t('email')} withArrow>
                                <Button
                                    size="compact-xs"
                                    radius="md"
                                    variant="light"
                                    color="blue"
                                    px={6}
                                    className="shrink-0 h-[28px]"
                                    onClick={() => {
                                        setShowEmail(true);
                                        setTimeout(() => emailInputRef.current?.focus(), 50);
                                    }}
                                >
                                    <IconMail size={15} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>

                    {showPhone2 && (
                        <div className="flex items-center gap-1 flex-1 min-w-[115px]">
                            <TextInput
                                ref={phone2InputRef}
                                size="xs"
                                type="tel"
                                placeholder={t('phone_2')}
                                leftSection={<IconPhone size={14} className="text-blue-500" />}
                                value={phone_2}
                                disabled={createPersonMutation.isPending}
                                onFocus={() => setHighlightedIndex(-1)}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setPhone2(val);
                                    setActiveSearchValue(val);
                                    setHighlightedIndex(-1);
                                }}
                                onKeyDown={handleKeyDown}
                                className="font-semibold flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={28}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setPhone2('');
                                    setShowPhone2(false);
                                }}
                            >
                                <IconX size={13} />
                            </ActionIcon>
                        </div>
                    )}

                    {showDoc && (
                        <div className="flex items-center gap-1 flex-1 min-w-[115px]">
                            <TextInput
                                ref={docInputRef}
                                size="xs"
                                placeholder={t('id_number')}
                                leftSection={<IconId size={14} className="text-blue-500" />}
                                value={document_number}
                                disabled={createPersonMutation.isPending}
                                onFocus={() => setHighlightedIndex(-1)}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setDocument(val);
                                    setActiveSearchValue(val);
                                    setHighlightedIndex(-1);
                                }}
                                onKeyDown={handleKeyDown}
                                className="font-semibold flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={28}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setDocument('');
                                    setShowDoc(false);
                                }}
                            >
                                <IconX size={13} />
                            </ActionIcon>
                        </div>
                    )}

                    {showEmail && (
                        <div className="flex items-center gap-1 flex-1 min-w-[125px]">
                            <TextInput
                                ref={emailInputRef}
                                size="xs"
                                type="email"
                                placeholder={t('email')}
                                leftSection={<IconMail size={14} className="text-blue-500" />}
                                value={email}
                                disabled={createPersonMutation.isPending}
                                onFocus={() => setHighlightedIndex(-1)}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setEmail(val);
                                    setActiveSearchValue(val);
                                    setHighlightedIndex(-1);
                                }}
                                onKeyDown={handleKeyDown}
                                className="flex-1 min-w-0"
                            />
                            <ActionIcon
                                size={28}
                                radius="md"
                                variant="subtle"
                                color="gray"
                                className="shrink-0"
                                onClick={() => {
                                    setEmail('');
                                    setShowEmail(false);
                                }}
                            >
                                <IconX size={13} />
                            </ActionIcon>
                        </div>
                    )}

                    <Button
                        onClick={save}
                        loading={createPersonMutation.isPending}
                        disabled={!canCreateNewPax}
                        size="xs"
                        radius="xl"
                        color="black"
                        leftSection={<IconUserPlus size={14} />}
                        className="shrink-0 ml-auto"
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>

            {/* Right Side: Matched Paxs Panel */}
            <div className="lg:col-span-4 bg-white rounded-lg border border-slate-200 flex flex-col overflow-hidden min-h-[118px] max-h-[126px]">
                <div className="px-2.5 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2 shrink-0">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                        <IconSearch size={12} className="text-slate-400" />
                        <span>{t('matched_passengers')}</span>
                    </div>
                    {isSearching ? (
                        <Loader size={12} color="gray" />
                    ) : debouncedSearchQuery.length >= MIN_CHARS_TO_SEARCH && matchingPaxs.length > 0 ? (
                        <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded-full bg-blue-50 text-blue-600">
                            {matchingPaxs.length}
                        </span>
                    ) : null}
                </div>

                <div className="flex-1 overflow-y-auto subtle-scrollbar divide-y divide-slate-100">
                    {debouncedSearchQuery.length < MIN_CHARS_TO_SEARCH ? (
                        null
                        // <div className="h-full flex items-center justify-center px-3 text-center text-[11px] text-slate-400">
                        //     {t('matched_passengers_hint')}
                        // </div>
                    ) : isSearching && matchingPaxs.length === 0 ? (
                        <div className="h-full flex items-center justify-center px-3 text-center text-[11px] text-slate-400">
                            {t('searching')}...
                        </div>
                    ) : matchingPaxs.length === 0 ? (
                        <div className="h-full flex items-center justify-center px-3 text-center text-[11px] text-slate-400">
                            {t('no_results')}
                        </div>
                    ) : (
                        matchingPaxs.map((pax, idx) => {
                            const hasDoc = !!(pax.document_type || pax.document_number);
                            const hasMail = !!pax.email;
                            const hasPhone = !!pax.phone;
                            const docStr = [pax.document_type, pax.document_number].filter(Boolean).join(' ');
                            const isHighlighted = highlightedIndex === idx;

                            return (
                                <div
                                    key={pax.id}
                                    className={`px-2.5 py-1.5 transition-colors cursor-pointer flex items-center justify-between gap-2 group ${isHighlighted ? 'bg-blue-50/90' : 'hover:bg-blue-50/60'}`}
                                    onClick={() => handleSelectPax(pax)}
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="flex flex-col items-center justify-center min-w-[28px] select-none shrink-0">
                                            {pax.category && <TagIcon name={pax.category} hideText size="xs" />}
                                            <span className="font-light text-[9px] text-slate-400 tracking-tight">#{pax.id}</span>
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-semibold text-xs text-slate-900 group-hover:text-blue-700 transition-colors truncate">
                                                    {pax.name}
                                                </span>
                                                {pax.discount_category && (
                                                    <span className="px-1 py-0.2 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shrink-0">
                                                        {pax.discount_category}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
                                                {hasDoc && (
                                                    <span className="inline-flex items-center gap-0.5 text-slate-700 font-medium">
                                                        <IconId size={10} className="text-slate-400 shrink-0" />
                                                        {docStr}
                                                    </span>
                                                )}
                                                {hasPhone && (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <IconPhone size={10} className="text-slate-400 shrink-0" />
                                                        {pax.phone}
                                                    </span>
                                                )}
                                                {hasMail && (
                                                    <span className="inline-flex items-center gap-0.5 truncate max-w-[120px]">
                                                        <IconMail size={10} className="text-slate-400 shrink-0" />
                                                        <span className="truncate">{pax.email}</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors shrink-0 inline-flex items-center gap-1 ${isHighlighted ? 'text-blue-700 bg-blue-100/90' : 'text-slate-500 bg-slate-100 group-hover:text-blue-600 group-hover:bg-blue-100/70'}`}>
                                        <span>{t('select_and_add')}</span>
                                        {isHighlighted && <kbd className="font-mono text-[9px]">↵</kbd>}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
